import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthGuard } from '@/hooks/useAuth';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabaseClient';
import { ILCELER, ARAC_TIPLERI, PARA_BIRIMLERI, TRANSFER_TIPLERI, ALINIS_YERLERI, BIRAKIS_YERLERI } from '@/lib/constants';

interface FormErrors {
  [key: string]: string;
}

const odemeDurumlari = ['Ödendi', 'Ödeme Bekliyor', 'Kısmi Ödeme', 'Araçda Ödeme', 'Hesaba Havale'];
const rezervasyonDurumlari = ['Yeni Rezervasyon', 'Onaylandı', 'Araç Atandı', 'Transfer Tamamlandı', 'İptal Edildi', 'Ödeme Bekliyor'];

const defaultFormData = {
  musteriAdi: '',
  telefon: '',
  transferTipi: 'Tek Yön',
  gelisTarihi: '',
  gelisSaati: '',
  alinisSaati: '',
  ucakKodu: '',
  alinisYeri: 'Antalya Havalimanı',
  birakYeri: '',
  kisiSayisi: 1,
  donusTarihi: '',
  donusSaati: '',
  donusAlinis: '',
  donusBirak: '',
  ilce: '',
  aracTipi: 'Mercedes Vito',
  paraBirimi: 'EUR',
  ucret: '',
  odemeDurumu: 'Ödeme Bekliyor',
  atananArac: '',
  atananPlaka: '',
  sofor: '',
  durum: 'Yeni Rezervasyon',
  donusDurum: '',
  notlar: '',
  cocukKoltukSayisi: 0,
  ozelTalepler: '',
};

export default function RezervasyonFormPage() {
  useAuthGuard();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const editId = searchParams.get('edit');
  const isEditMode = Boolean(editId);

  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [basarili, setBasarili] = useState(false);
  const [basariliRezNo, setBasariliRezNo] = useState('');
  const [step, setStep] = useState(1);
  const [yukleniyor, setYukleniyor] = useState(isEditMode);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [editDbId, setEditDbId] = useState<number | null>(null);
  const [drivers, setDrivers] = useState<{ id: number; ad_soyad: string }[]>([]);

  const mapDbToForm = (rez: Record<string, unknown>) => ({
    musteriAdi: String(rez.musteri_adi || ''),
    telefon: String(rez.telefon || ''),
    transferTipi: String(rez.transfer_tipi || 'Tek Yön'),
    gelisTarihi: String(rez.gelis_tarihi || ''),
    gelisSaati: rez.gelis_saati ? String(rez.gelis_saati).substring(0, 5) : '',
    alinisSaati: rez.alinis_saati ? String(rez.alinis_saati).substring(0, 5) : '',
    ucakKodu: String(rez.ucak_kodu || ''),
    alinisYeri: String(rez.alinis_yeri || 'Antalya Havalimanı'),
    birakYeri: String(rez.birak_yeri || ''),
    kisiSayisi: Number(rez.kisi_sayisi) || 1,
    donusTarihi: String(rez.donus_tarihi || ''),
    donusSaati: rez.donus_saati ? String(rez.donus_saati).substring(0, 5) : '',
    donusAlinis: String(rez.donus_alinis || ''),
    donusBirak: String(rez.donus_birak || ''),
    ilce: String(rez.ilce || ''),
    aracTipi: String(rez.arac_tipi || 'Mercedes Vito'),
    paraBirimi: String(rez.para_birimi || 'EUR'),
    ucret: String(rez.ucret || ''),
    odemeDurumu: String(rez.odeme_durumu || 'Ödeme Bekliyor'),
    atananArac: String(rez.atanan_arac || ''),
    atananPlaka: String(rez.atanan_plaka || ''),
    sofor: String(rez.sofor || ''),
    durum: String(rez.durum || 'Yeni Rezervasyon'),
    donusDurum: String(rez.donus_durum || ''),
    notlar: String(rez.notlar || ''),
    cocukKoltukSayisi: Number(rez.cocuk_koltuk_sayisi) || 0,
    ozelTalepler: String(rez.ozel_talepler || ''),
  });

  const mapFormToDb = () => ({
    musteri_adi: formData.musteriAdi,
    telefon: formData.telefon,
    transfer_tipi: formData.transferTipi,
    gelis_tarihi: formData.gelisTarihi,
    gelis_saati: formData.gelisSaati,
    alinis_saati: formData.alinisSaati || null,
    ucak_kodu: formData.ucakKodu || null,
    alinis_yeri: formData.alinisYeri,
    birak_yeri: formData.birakYeri,
    kisi_sayisi: formData.kisiSayisi,
    donus_tarihi: formData.donusTarihi || null,
    donus_saati: formData.donusSaati || null,
    donus_alinis: formData.donusAlinis || null,
    donus_birak: formData.donusBirak || null,
    ilce: formData.ilce,
    arac_tipi: formData.aracTipi || null,
    para_birimi: formData.paraBirimi,
    ucret: Number(formData.ucret),
    odeme_durumu: formData.odemeDurumu,
    atanan_arac: formData.atananArac || null,
    atanan_plaka: formData.atananPlaka || null,
    sofor: formData.sofor || null,
    durum: formData.durum,
    donus_durum: formData.transferTipi === 'Gidiş-Dönüş' ? (formData.donusDurum || formData.durum) : null,
    notlar: formData.notlar || null,
    cocuk_koltuk_sayisi: formData.cocukKoltukSayisi,
    ozel_talepler: formData.ozelTalepler || null,
  });

  // Load existing reservation data in edit mode
  useEffect(() => {
    if (!editId) return;

    const numericId = Number(editId);
    const isDbId = !isNaN(numericId) && numericId > 0;

    if (isDbId) {
      // Fetch from Supabase
      setYukleniyor(true);
      supabase.functions.invoke('manage-reservations', {
        body: { action: 'getById', id: numericId },
      }).then(({ data, error }) => {
        if (error || !data?.success || !data?.data) {
          setYukleniyor(false);
          return;
        }
        const rez = data.data;
        setEditDbId(numericId);
        setFormData(mapDbToForm(rez));
        setBasariliRezNo(String(rez.rezervasyon_no));
        setYukleniyor(false);
      });
    } else {
      // Geçersiz ID formatı, formu boş aç
      setYukleniyor(false);
    }
  }, [editId]);

  // Load drivers list for dropdown
  useEffect(() => {
    supabase.from('drivers').select('id, ad_soyad').eq('aktif', true).order('ad_soyad')
      .then(({ data, error }) => {
        if (!error && data) {
          setDrivers(data);
        }
      });
  }, []);

  const handleChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    }
  };

  const validateStep = (s: number): boolean => {
    const newErrors: FormErrors = {};
    if (s === 1) {
      if (!formData.musteriAdi.trim()) newErrors.musteriAdi = 'Müşteri adı soyadı zorunludur';
      if (!formData.telefon.trim()) newErrors.telefon = 'Telefon numarası zorunludur';
      if (!formData.gelisTarihi) newErrors.gelisTarihi = 'Geliş tarihi zorunludur';
      if (!formData.gelisSaati) newErrors.gelisSaati = 'Geliş saati zorunludur';
      if (!formData.alinisSaati) newErrors.alinisSaati = 'Alınış saati zorunludur';
      if (!formData.ilce) newErrors.ilce = 'İlçe / bölge seçimi zorunludur';
      if (formData.kisiSayisi < 1) newErrors.kisiSayisi = 'En az 1 kişi olmalıdır';
    }
    if (s === 2) {
      if (!formData.aracTipi) newErrors.aracTipi = 'Araç tipi seçimi zorunludur';
      if (!formData.ucret || Number(formData.ucret) <= 0) newErrors.ucret = 'Geçerli bir ücret giriniz';
      if (!formData.paraBirimi) newErrors.paraBirimi = 'Para birimi seçimi zorunludur';
    }
    if (s === 3) {
      if (!formData.durum) newErrors.durum = 'Rezervasyon durumu zorunludur';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) setStep(step + 1);
  };

  const handlePrev = () => {
    setStep(step - 1);
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateStep(step)) return;
    setGonderiliyor(true);
    setSubmitError(null);

    try {
      const dbData = mapFormToDb();

      let response;
      if (isEditMode && editDbId) {
        response = await supabase.functions.invoke('manage-reservations', {
          body: { action: 'update', id: editDbId, data: dbData },
        });
      } else {
        response = await supabase.functions.invoke('manage-reservations', {
          body: { action: 'create', data: dbData },
        });
      }

      // When the edge function returns non-2xx, supabase puts the body in response.error
      // Try to extract the real error message from the response body first
      let errorMsg: string | null = null;
      if (response.error) {
        // The error might be a FunctionsHttpError with a raw body we can read
        const raw = await (response.error as unknown as { body?: () => Promise<string> }).body?.();
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            errorMsg = parsed.error || parsed.message || null;
          } catch {
            errorMsg = raw;
          }
        }
        if (!errorMsg) {
          errorMsg = (response.error as Error).message || 'Kayıt başarısız oldu.';
        }
        throw new Error(errorMsg);
      }

      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Kayıt başarısız oldu.');
      }

      const saved = response.data.data;
      setBasariliRezNo(String(saved.rezervasyon_no));
      setBasarili(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Beklenmeyen bir hata oluştu.';
      setSubmitError(msg);
    } finally {
      setGonderiliyor(false);
    }
  };

  const handleNewReservation = () => {
    setFormData(defaultFormData);
    setErrors({});
    setSubmitError(null);
    setEditDbId(null);
    setBasarili(false);
    setBasariliRezNo('');
    setStep(1);
    navigate('/rezervasyonlar/yeni');
  };

  const handleCancel = () => {
    navigate('/rezervasyonlar');
  };

  const inputBase = 'w-full bg-vip-black border rounded-md px-3 py-2.5 text-sm text-white focus:outline-none transition-colors';
  const inputOk = `${inputBase} border-vip-gold/15 focus:border-vip-gold/40`;
  const inputErr = `${inputBase} border-red-500/40 focus:border-red-500/60`;
  const labelBase = 'block text-xs text-gray-400 mb-1.5';

  const uygunIlceler = ILCELER.filter(i => i !== 'Antalya Havalimanı');

  if (yukleniyor) {
    return (
      <AdminLayout>
        <div className="mb-6">
          <h2 className="text-xl md:text-2xl font-display font-bold text-white">
            {isEditMode ? 'Rezervasyon Düzenle' : 'Yeni Rezervasyon'}
          </h2>
        </div>
        <div className="max-w-3xl bg-vip-dark border border-vip-gold/10 rounded-lg p-12 text-center">
          <i className="ri-loader-4-line text-3xl text-vip-gold animate-spin"></i>
          <p className="text-sm text-gray-400 mt-3">Rezervasyon bilgileri yükleniyor...</p>
        </div>
      </AdminLayout>
    );
  }

  if (basarili) {
    return (
      <AdminLayout>
        <div className="mb-6">
          <h2 className="text-xl md:text-2xl font-display font-bold text-white">
            {isEditMode ? 'Rezervasyon Düzenle' : 'Yeni Rezervasyon'}
          </h2>
        </div>

        <div className="max-w-2xl mx-auto bg-vip-dark border border-vip-gold/10 rounded-lg p-8 md:p-12 text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center mx-auto mb-6">
            <i className="ri-check-double-line text-3xl text-emerald-400"></i>
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            {isEditMode ? 'Rezervasyon Güncellendi!' : 'Rezervasyon Başarıyla Oluşturuldu!'}
          </h3>
          <p className="text-sm text-gray-400 mb-6">
            {isEditMode ? 'Değişiklikler sisteme kaydedildi.' : 'Yeni rezervasyonunuz sisteme kaydedildi.'}
          </p>

          <div className="bg-vip-black/50 rounded-lg p-4 mb-8 max-w-sm mx-auto">
            <p className="text-xs text-gray-500 mb-1">Rezervasyon Numarası</p>
            <p className="text-lg font-mono font-bold text-vip-gold">{basariliRezNo}</p>
            <div className="mt-3 text-xs text-gray-400 space-y-1">
              <p><i className="ri-user-line mr-1"></i>{formData.musteriAdi}</p>
              <p><i className="ri-map-pin-line mr-1"></i>{formData.alinisYeri} → {formData.birakYeri || 'Yok'}</p>
              <p><i className="ri-calendar-line mr-1"></i>{formData.gelisTarihi} {formData.gelisSaati} (Alınış: {formData.alinisSaati || '-'})</p>
              {formData.transferTipi === 'Gidiş-Dönüş' && formData.donusTarihi && (
                <p><i className="ri-arrow-go-back-line mr-1"></i>Dönüş: {formData.donusTarihi} {formData.donusSaati} ({formData.donusAlinis || '?'} → {formData.donusBirak || '?'})</p>
              )}
              <p><i className="ri-money-euro-circle-line mr-1"></i>{formData.ucret} {formData.paraBirimi}</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleNewReservation}
              className="px-6 py-2.5 bg-vip-gold text-vip-black font-semibold rounded-md hover:bg-vip-gold-light transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>
              Yeni Rezervasyon
            </button>
            <button
              onClick={() => navigate('/rezervasyonlar')}
              className="px-6 py-2.5 bg-vip-black text-gray-300 font-medium rounded-md border border-vip-gold/15 hover:text-white hover:border-vip-gold/30 transition-colors whitespace-nowrap"
            >
              <i className="ri-list-check mr-2"></i>
              Rezervasyonlar Listesi
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-display font-bold text-white">
            {isEditMode ? `Rezervasyon Düzenle — ${basariliRezNo}` : 'Yeni Rezervasyon'}
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            {isEditMode ? 'Mevcut rezervasyonu düzenleyin' : 'Yeni transfer rezervasyonu oluşturun'}
          </p>
        </div>
        <button
          onClick={handleCancel}
          className="px-4 py-2 bg-vip-black text-gray-400 text-sm font-medium rounded-md border border-vip-gold/15 hover:text-white hover:border-vip-gold/30 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line mr-1"></i>
          İptal
        </button>
      </div>

      {/* Submit Error Banner */}
      {submitError && (
        <div className="max-w-3xl mb-4 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 flex items-start gap-3">
          <i className="ri-error-warning-line text-red-400 text-base mt-0.5 flex-shrink-0"></i>
          <div>
            <p className="text-sm font-medium text-red-400">Kayıt Başarısız</p>
            <p className="text-xs text-red-400/80 mt-0.5">{submitError}</p>
          </div>
          <button onClick={() => setSubmitError(null)} className="ml-auto text-red-400/60 hover:text-red-400">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {/* Progress Steps */}
      <div className="flex items-center gap-2 mb-8 max-w-3xl">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                s === step
                  ? 'bg-vip-gold text-vip-black'
                  : s < step
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-vip-black text-gray-500 border border-vip-gold/10'
              }`}
            >
              {s < step ? (
                <i className="ri-check-line text-sm"></i>
              ) : (
                s
              )}
            </div>
            <span
              className={`text-xs font-medium hidden sm:block ${
                s === step ? 'text-vip-gold' : s < step ? 'text-emerald-400' : 'text-gray-500'
              }`}
            >
              {s === 1 && 'Müşteri & Rota'}
              {s === 2 && 'Araç & Fiyat'}
              {s === 3 && 'Onay & Kaydet'}
            </span>
            {s < 3 && (
              <div className={`flex-1 h-px ${s < step ? 'bg-emerald-500/30' : 'bg-vip-gold/10'}`}></div>
            )}
          </div>
        ))}
      </div>

      <div className="max-w-3xl">
        {/* STEP 1: Müşteri & Rota */}
        {step === 1 && (
          <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5 md:p-6 space-y-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-md bg-vip-gold/15 border border-vip-gold/20 flex items-center justify-center">
                <i className="ri-user-line text-vip-gold text-xs"></i>
              </div>
              <h3 className="text-sm font-semibold text-white">Müşteri Bilgileri</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>
                  Müşteri Adı Soyadı <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.musteriAdi}
                  onChange={e => handleChange('musteriAdi', e.target.value)}
                  placeholder="örn: Ahmet Yılmaz"
                  className={errors.musteriAdi ? inputErr : inputOk}
                />
                {errors.musteriAdi && <p className="text-xs text-red-400 mt-1">{errors.musteriAdi}</p>}
              </div>
              <div>
                <label className={labelBase}>
                  Telefon <span className="text-red-400">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.telefon}
                  onChange={e => handleChange('telefon', e.target.value)}
                  placeholder="örn: +90 532 456 78 90"
                  className={errors.telefon ? inputErr : inputOk}
                />
                {errors.telefon && <p className="text-xs text-red-400 mt-1">{errors.telefon}</p>}
              </div>
            </div>

            <div className="border-t border-vip-gold/10 pt-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 rounded-md bg-vip-gold/15 border border-vip-gold/20 flex items-center justify-center">
                  <i className="ri-route-line text-vip-gold text-xs"></i>
                </div>
                <h3 className="text-sm font-semibold text-white">Transfer Detayları</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelBase}>
                    Transfer Türü <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={formData.transferTipi}
                    onChange={e => handleChange('transferTipi', e.target.value)}
                    className={inputOk}
                  >
                    {TRANSFER_TIPLERI.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelBase}>
                    Kişi Sayısı <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={formData.kisiSayisi}
                    onChange={e => handleChange('kisiSayisi', Number(e.target.value))}
                    className={errors.kisiSayisi ? inputErr : inputOk}
                  />
                  {errors.kisiSayisi && <p className="text-xs text-red-400 mt-1">{errors.kisiSayisi}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className={labelBase}>
                    Geliş Tarihi <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.gelisTarihi}
                    onChange={e => handleChange('gelisTarihi', e.target.value)}
                    className={errors.gelisTarihi ? inputErr : inputOk}
                  />
                  {errors.gelisTarihi && <p className="text-xs text-red-400 mt-1">{errors.gelisTarihi}</p>}
                </div>
                <div>
                  <label className={labelBase}>
                    Geliş Saati <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="time"
                    value={formData.gelisSaati}
                    onChange={e => handleChange('gelisSaati', e.target.value)}
                    className={errors.gelisSaati ? inputErr : inputOk}
                  />
                  {errors.gelisSaati && <p className="text-xs text-red-400 mt-1">{errors.gelisSaati}</p>}
                </div>
                <div>
                  <label className={labelBase}>
                    Alınış Saati <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="time"
                    value={formData.alinisSaati}
                    onChange={e => handleChange('alinisSaati', e.target.value)}
                    className={errors.alinisSaati ? inputErr : inputOk}
                  />
                  {errors.alinisSaati && <p className="text-xs text-red-400 mt-1">{errors.alinisSaati}</p>}
                </div>
              </div>

              <div>
                <label className={labelBase}>Uçak Kodu</label>
                <input
                  type="text"
                  value={formData.ucakKodu}
                  onChange={e => handleChange('ucakKodu', e.target.value)}
                  placeholder="örn: TK-2145"
                  className={inputOk}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className={labelBase}>
                    Alınış Yeri <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={formData.alinisYeri}
                    onChange={e => handleChange('alinisYeri', e.target.value)}
                    className={inputOk}
                  >
                    {ALINIS_YERLERI.map(i => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelBase}>
                    Bırakış Yeri
                  </label>
                  <select
                    value={formData.birakYeri}
                    onChange={e => handleChange('birakYeri', e.target.value)}
                    className={inputOk}
                  >
                    {BIRAKIS_YERLERI.map(i => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelBase}>
                  İlçe / Bölge <span className="text-red-400">*</span>
                </label>
                <select
                  value={formData.ilce}
                  onChange={e => handleChange('ilce', e.target.value)}
                  className={errors.ilce ? inputErr : inputOk}
                >
                  <option value="">İlçe seçiniz</option>
                  {uygunIlceler.map(i => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
                {errors.ilce && <p className="text-xs text-red-400 mt-1">{errors.ilce}</p>}
              </div>

              {formData.transferTipi === 'Gidiş-Dönüş' && (
                <div className="border border-vip-gold/10 rounded-lg p-4 mt-4 space-y-4 bg-vip-black/30">
                  <div className="flex items-center gap-2">
                    <i className="ri-arrow-left-right-line text-vip-gold text-xs"></i>
                    <h4 className="text-sm font-medium text-white">Dönüş Bilgileri</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelBase}>Dönüş Tarihi</label>
                      <input
                        type="date"
                        value={formData.donusTarihi}
                        onChange={e => handleChange('donusTarihi', e.target.value)}
                        className={inputOk}
                      />
                    </div>
                    <div>
                      <label className={labelBase}>Dönüş Saati</label>
                      <input
                        type="time"
                        value={formData.donusSaati}
                        onChange={e => handleChange('donusSaati', e.target.value)}
                        className={inputOk}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelBase}>Dönüş Alınış</label>
                      <input
                        type="text"
                        value={formData.donusAlinis}
                        onChange={e => handleChange('donusAlinis', e.target.value)}
                        placeholder="örn: Otel Adı"
                        className={inputOk}
                      />
                    </div>
                    <div>
                      <label className={labelBase}>Dönüş Bırakış</label>
                      <input
                        type="text"
                        value={formData.donusBirak}
                        onChange={e => handleChange('donusBirak', e.target.value)}
                        placeholder="örn: Antalya Havalimanı"
                        className={inputOk}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className={labelBase}>Çocuk Koltuğu Sayısı</label>
                  <input
                    type="number"
                    min={0}
                    max={5}
                    value={formData.cocukKoltukSayisi}
                    onChange={e => handleChange('cocukKoltukSayisi', Number(e.target.value))}
                    className={inputOk}
                  />
                </div>
                <div>
                  <label className={labelBase}>Özel Talepler</label>
                  <input
                    type="text"
                    value={formData.ozelTalepler}
                    onChange={e => handleChange('ozelTalepler', e.target.value)}
                    placeholder="örn: Bisiklet taşıma, engelli asansörü"
                    className={inputOk}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-3">
              <button
                onClick={handleNext}
                className="px-6 py-2.5 bg-vip-gold text-vip-black font-semibold rounded-md hover:bg-vip-gold-light transition-colors whitespace-nowrap"
              >
                İleri <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Araç & Fiyat */}
        {step === 2 && (
          <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5 md:p-6 space-y-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-md bg-vip-gold/15 border border-vip-gold/20 flex items-center justify-center">
                <i className="ri-roadster-line text-vip-gold text-xs"></i>
              </div>
              <h3 className="text-sm font-semibold text-white">Araç & Fiyat Bilgileri</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>
                  Araç Tipi <span className="text-red-400">*</span>
                </label>
                <select
                  value={formData.aracTipi}
                  onChange={e => {
                    handleChange('aracTipi', e.target.value);
                    handleChange('atananArac', '');
                  }}
                  className={errors.aracTipi ? inputErr : inputOk}
                >
                  {ARAC_TIPLERI.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                {errors.aracTipi && <p className="text-xs text-red-400 mt-1">{errors.aracTipi}</p>}
              </div>
              <div>
                <label className={labelBase}>
                  Para Birimi <span className="text-red-400">*</span>
                </label>
                <select
                  value={formData.paraBirimi}
                  onChange={e => handleChange('paraBirimi', e.target.value)}
                  className={errors.paraBirimi ? inputErr : inputOk}
                >
                  {PARA_BIRIMLERI.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                {errors.paraBirimi && <p className="text-xs text-red-400 mt-1">{errors.paraBirimi}</p>}
              </div>
            </div>

            <div>
              <label className={labelBase}>
                Transfer Ücreti <span className="text-red-400">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={formData.ucret}
                  onChange={e => handleChange('ucret', e.target.value)}
                  placeholder="0.00"
                  className={`${errors.ucret ? inputErr : inputOk} flex-1`}
                />
                <span className="text-sm text-gray-400 whitespace-nowrap">{formData.paraBirimi}</span>
              </div>
              {errors.ucret && <p className="text-xs text-red-400 mt-1">{errors.ucret}</p>}
            </div>

            <div>
              <label className={labelBase}>Ödeme Durumu</label>
              <select
                value={formData.odemeDurumu}
                onChange={e => handleChange('odemeDurumu', e.target.value)}
                className={inputOk}
              >
                {odemeDurumlari.map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>

            <div className="border-t border-vip-gold/10 pt-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 rounded-md bg-vip-gold/15 border border-vip-gold/20 flex items-center justify-center">
                  <i className="ri-steering-line text-vip-gold text-xs"></i>
                </div>
                <h3 className="text-sm font-semibold text-white">Araç Atama</h3>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Araç ve şoför bilgilerini 3. adımda (Onay & Kaydet) manuel olarak girebilirsiniz.
              </p>
            </div>

            <div className="flex justify-between pt-3">
              <button
                onClick={handlePrev}
                className="px-6 py-2.5 bg-vip-black text-gray-300 font-medium rounded-md border border-vip-gold/15 hover:text-white hover:border-vip-gold/30 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-1"></i> Geri
              </button>
              <button
                onClick={handleNext}
                className="px-6 py-2.5 bg-vip-gold text-vip-black font-semibold rounded-md hover:bg-vip-gold-light transition-colors whitespace-nowrap"
              >
                İleri <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Onay & Kaydet */}
        {step === 3 && (
          <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5 md:p-6 space-y-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-md bg-vip-gold/15 border border-vip-gold/20 flex items-center justify-center">
                <i className="ri-check-double-line text-vip-gold text-xs"></i>
              </div>
              <h3 className="text-sm font-semibold text-white">Rezervasyon Özeti & Durum</h3>
            </div>

            {/* Summary Card */}
            <div className="bg-vip-black/50 border border-vip-gold/10 rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-xs text-gray-500 block">Müşteri</span>
                  <span className="text-white font-medium">{formData.musteriAdi}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block">Telefon</span>
                  <span className="text-gray-300">{formData.telefon}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block">Transfer Türü</span>
                  <span className="text-gray-300">{formData.transferTipi}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block">Kişi Sayısı</span>
                  <span className="text-gray-300">{formData.kisiSayisi} Kişi</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block">Geliş</span>
                  <span className="text-gray-300">{formData.gelisTarihi} {formData.gelisSaati}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block">Alınış Saati</span>
                  <span className="text-gray-300">{formData.alinisSaati || '-'}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block">Uçak Kodu</span>
                  <span className="text-gray-300">{formData.ucakKodu || '-'}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block">Rota</span>
                  <span className="text-gray-300">{formData.alinisYeri} → {formData.birakYeri || 'Yok'}</span>
                </div>
                {formData.transferTipi === 'Gidiş-Dönüş' && formData.donusTarihi && (
                  <>
                    <div>
                      <span className="text-xs text-gray-500 block">Dönüş Tarihi</span>
                      <span className="text-gray-300">{formData.donusTarihi} {formData.donusSaati}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Dönüş Rota</span>
                      <span className="text-gray-300">{formData.donusAlinis || '?'} → {formData.donusBirak || '?'}</span>
                    </div>
                  </>
                )}
                <div>
                  <span className="text-xs text-gray-500 block">İlçe</span>
                  <span className="text-gray-300">{formData.ilce}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block">Araç Tipi</span>
                  <span className="text-gray-300">{formData.aracTipi}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block">Ücret</span>
                  <span className="text-vip-gold font-semibold">{formData.ucret} {formData.paraBirimi}</span>
                </div>
                {(formData.atananArac || formData.atananPlaka || formData.sofor) && (
                  <>
                    <div>
                      <span className="text-xs text-gray-500 block">Atanan Araç</span>
                      <span className="text-gray-300">{formData.atananArac || '-'}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Plaka</span>
                      <span className="text-gray-300">{formData.atananPlaka || '-'}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Şoför</span>
                      <span className="text-gray-300">{formData.sofor || '-'}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>
                  Rezervasyon Durumu <span className="text-red-400">*</span>
                </label>
                <select
                  value={formData.durum}
                  onChange={e => handleChange('durum', e.target.value)}
                  className={errors.durum ? inputErr : inputOk}
                >
                  {rezervasyonDurumlari.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                {errors.durum && <p className="text-xs text-red-400 mt-1">{errors.durum}</p>}
              </div>
              {formData.transferTipi === 'Gidiş-Dönüş' && (
                <div>
                  <label className={labelBase}>Dönüş Durumu</label>
                  <select
                    value={formData.donusDurum || formData.durum}
                    onChange={e => handleChange('donusDurum', e.target.value)}
                    className={inputOk}
                  >
                    {rezervasyonDurumlari.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  <p className="text-[10px] text-gray-500 mt-1">Dönüş transferinin başlangıç durumu</p>
                </div>
              )}
              <div>
                <label className={labelBase}>Atanan Araç (Manuel)</label>
                <input
                  type="text"
                  value={formData.atananArac}
                  onChange={e => handleChange('atananArac', e.target.value)}
                  placeholder="örn: Mercedes Vito 01"
                  className={inputOk}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>Plaka (Manuel)</label>
                <input
                  type="text"
                  value={formData.atananPlaka}
                  onChange={e => handleChange('atananPlaka', e.target.value)}
                  placeholder="örn: 07 ABC 123"
                  className={inputOk}
                />
              </div>
              <div>
                <label className={labelBase}>Şoför</label>
                <select
                  value={formData.sofor}
                  onChange={e => handleChange('sofor', e.target.value)}
                  className={inputOk}
                >
                  <option value="">Şoför seçiniz</option>
                  {drivers.map(d => (
                    <option key={d.id} value={d.ad_soyad}>{d.ad_soyad}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className={labelBase}>Ek Notlar</label>
              <textarea
                value={formData.notlar}
                onChange={e => handleChange('notlar', e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Müşteri hakkında ek bilgiler, özel notlar..."
                className={inputOk}
              />
              <p className="text-[10px] text-gray-500 mt-1">{formData.notlar.length}/500 karakter</p>
            </div>

            <div className="flex justify-between pt-3">
              <button
                onClick={handlePrev}
                className="px-6 py-2.5 bg-vip-black text-gray-300 font-medium rounded-md border border-vip-gold/15 hover:text-white hover:border-vip-gold/30 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-1"></i> Geri
              </button>
              <button
                onClick={handleSubmit}
                disabled={gonderiliyor}
                className="px-6 py-2.5 bg-vip-gold text-vip-black font-semibold rounded-md hover:bg-vip-gold-light transition-colors whitespace-nowrap disabled:opacity-50 flex items-center gap-2"
              >
                {gonderiliyor ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    {isEditMode ? 'Güncelleniyor...' : 'Kaydediliyor...'}
                  </>
                ) : (
                  <>
                    <i className="ri-save-line"></i>
                    {isEditMode ? 'Değişiklikleri Kaydet' : 'Rezervasyonu Kaydet'}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}